import { createContext, Dispatch, FC, SetStateAction, useState } from 'react'
import { isEmpty, noop } from 'lodash'
import { useMount, useToggle, useUnmount } from 'react-use'
import useFormProcessor from '@hooks/useFormProcessor'
import AddNewItemModalLayout from '@templates/AddNewItemModalLayout'
import Tabs, { TabsItem } from '@organisms/Tabs'
import { Option } from '@organisms/Select'
import {
  useCreateRiskMapMutation,
  useUpdateRiskMapMutation,
} from '@services/riskMaps'
import { CommonRiskMapBody, RiskMapDetailed } from '@models/riskMap'
import { File } from '@models/file'
import { PositionOption } from '@models/position'
import { DivisionOption } from '@models/division'
import { FilialOption } from '@models/filial'
import { CompanyOption } from '@models/company'
import { useAppSelector } from '@hooks/useAppSelector'
import OnboardingCodes from '@enums/OnboardingCodes'
import Onboarding from '@templates/Onboarding'
import { getMultilineValue } from '@utils/form'
import { useAppDispatch } from '@hooks/useAppDispatch'
import { cleanAllForms } from '@services/riskMapModal'
import useSetDefaultProtectionMeans from '@modals/RiskMapModal/useSetDefaultProtectionMeans.'
import { AUTO_SIZ_NAME } from '@modals/RiskMapModalFromFile/useRiskMapFromFileFields'
import { hasValidationErrors } from '@utils/validation'
import { log } from '@utils/console'
import { MereFormItem } from '@modals/RiskMapModal/Dangers/DangersFields/DangersFieldsBlock/DangersFieldsMeresModalButton/MeresFields'
import { ProtectionMeanItem } from '@modals/RiskMapModal/Dangers/DangersFields/DangersFieldsBlock/DangersFieldsProtectionMeansModalButton/ProtectionMeansFields'
import useSetDefaultComissionMembers from './useSetDefaultComissionMembers'
import { TopTabsWrapper } from './styled'
import getDefaultValues from './getDefaultValues'
import MainRisks from './MainRisks'
import Dangers from './Dangers'
import useSetDefaultMeres from './useSetDefaultMeres'
import { getOnboardingSteps, ONBOARDING_TITLE } from './onboarding'

type TopControllersContextValue = {
  enabledEtn: boolean | undefined
  toggleEnabledEtn: Dispatch<SetStateAction<boolean>>
  enabledAutoAddSiz: boolean | undefined
  toggleEnabledAutoAddSiz: (nextValue: boolean) => void
}

export const TopControllersContext = createContext<TopControllersContextValue>({
  enabledEtn: undefined,
  enabledAutoAddSiz: undefined,
  toggleEnabledEtn: noop,
  toggleEnabledAutoAddSiz: noop,
})

export type DangerDTOInForm = {
  id?: string
  type?: Option
  kind?: Option
  event?: Option
  workPlace?: Option
  object?: Option
  hardGrade?: Option
  probability?: Option
  hazard?: Option
  selectedFromModal?: boolean
  notAutoAddControlMeasures: boolean
  notAutoAddProtectionMeans: boolean
  meres?: MereFormItem[]
  protectionMeans?: ProtectionMeanItem[]
}

export type FormValues = {
  company: CompanyOption
  filial: FilialOption
  division: DivisionOption
  position: PositionOption
  file?: File | null
  number: string
  reviseBeforeAt?: string
  approvedAt?: string
  riskMapCommissionMembers?: Option[]
  dangersControlMeasureWorkplace: Option
  autoAddProtectionMeans: boolean
  dangerDTOs: DangerDTOInForm[]
}

type Props = {
  isEdit?: boolean
  handleCloseModal: () => void
  riskMap?: Partial<RiskMapDetailed>
  initialActiveTopTab?: number
  onMount?: () => void
}

const RiskMapModal: FC<Props> = ({
  riskMap,
  isEdit = false,
  handleCloseModal,
  initialActiveTopTab = 0,
  onMount = noop,
}) => {
  useMount(onMount)
  const dispatch = useAppDispatch()
  const selectedMeresForms = useAppSelector(
    (state) => state.riskMapModal.selectedMeresForms
  )
  const selectedProtectionMeansForms = useAppSelector(
    (state) => state.riskMapModal.selectedProtectionMeansForms
  )
  const [active, setActive] = useState(initialActiveTopTab)
  const [enabledEtn, toggleEnabledEtn] = useToggle(
    riskMap?.typeOfName === 'all'
  )

  const [create, createData] = useCreateRiskMapMutation()
  const [update, updateData] = useUpdateRiskMapMutation()
  const additionalProps = isEdit ? updateData : createData

  const methods = useFormProcessor({
    apiErrors: undefined,
    defaultValues: getDefaultValues(riskMap),
  })

  const { watch, setValue, formState } = methods

  const enabledAutoAddSiz: boolean = watch(AUTO_SIZ_NAME)

  const toggleEnabledAutoAddSiz = (value: boolean) => {
    setValue(AUTO_SIZ_NAME, value)
  }

  useSetDefaultComissionMembers(methods, isEdit)
  useSetDefaultMeres(riskMap)
  useSetDefaultProtectionMeans(riskMap)

  const onModalClose = () => {
    dispatch(cleanAllForms())
    handleCloseModal()
  }

  useUnmount(onModalClose)

  const handleSubmitForm = (form: FormValues) => {
    log({
      selectedMeresForms,
      selectedProtectionMeansForms,
    })
    const commonForm: CommonRiskMapBody = {
      typeOfName: enabledEtn ? 'all' : 'only_etn',
      typeOfAddingProtectionMeans: enabledAutoAddSiz ? 'auto' : 'manual',
      number: form.number,
      jobPosition: form.position['@id'],
      reviseBeforeAt: form.reviseBeforeAt,
      approvedAt: form.approvedAt || null,
      file: form.file?.['@id'],
      autoAddProtectionMeans: enabledAutoAddSiz,
      dangersControlMeasureWorkplace:
        form.dangersControlMeasureWorkplace?.value || null,
      commissionMembers: getMultilineValue(form.riskMapCommissionMembers),
      dangerDTOs: !isEmpty(form.dangerDTOs?.[0]?.workPlace)
        ? form.dangerDTOs?.map(
            (
              {
                event,
                kind,
                workPlace,
                object,
                hardGrade,
                probability,
                type,
                hazard,
              },
              index
            ) => {
              const meres = selectedMeresForms[index]?.meres || []
              const notAutoAddControlMeasures =
                !!selectedMeresForms[index]?.notAutoAddControlMeasures
              const controlMeasures = isEmpty(meres[0])
                ? []
                : meres.map(
                    ({
                      mereKind,
                      content,
                      source,
                      completionStartDate,
                      completionEndDate,
                      eventRepetitionFrequency,
                      responsible,
                    }) => ({
                      kind: mereKind?.label,
                      content: content?.label,
                      fundingSource: source,
                      completionStartDate,
                      completionEndDate,
                      eventRepetitionFrequency,
                      responsiblePerson: responsible?.['@id'],
                    })
                  )

              const means =
                selectedProtectionMeansForms[index]?.protectionMeans || []
              const notAutoAddProtectionMeans =
                !!selectedProtectionMeansForms[index]?.notAutoAddProtectionMeans

              const protectionMeans = isEmpty(means[0])
                ? []
                : means.map(
                    ({
                      type,
                      constructionMean,
                      quantity,
                      updateFrequency,
                      updateBeforeAt,
                      etnNumber,
                      additionalType,
                    }) => ({
                      type: type?.label,
                      etnNumber,
                      constructionMean: constructionMean?.label,
                      quantity,
                      updateFrequency: updateFrequency
                        ? +updateFrequency
                        : undefined,
                      updateBeforeAt: updateBeforeAt || undefined,
                      additionalProtectionMean: !!additionalType,
                      additionalType,
                    })
                  )

              return {
                hazard: hazard?.label,
                type: type?.value,
                event: event?.label,
                kind: kind?.label,
                object: object?.label,
                workPlace: workPlace?.label,
                injuryPossibility: probability?.value,
                injurySeverity: hardGrade?.value,
                controlMeasures,
                protectionMeans,
                notAutoAddControlMeasures,
                notAutoAddProtectionMeans,
              }
            }
          )
        : [],
    }

    log('commonForm', commonForm)

    const dangers = commonForm.dangerDTOs
    log('dangers', dangers)
    const means = commonForm.dangerDTOs.reduce(
      (acc, item) => [...acc, ...item.controlMeasures],
      [] as any
    )
    log('means', means)

    const uniqueDangers: any[] = commonForm.dangerDTOs.reduce((acc, item) => {
      const hasItemInAccumulator = acc.some(
        (accItem: any) =>
          accItem.hazard === item.hazard && accItem.event === item.event
      )

      if (!hasItemInAccumulator) {
        acc.push(item)
      }

      return acc
    }, [] as any)
    log('uniqueDangers', uniqueDangers)
    const uniqueMeans = uniqueDangers.reduce(
      (acc, item) => [...acc, ...item.controlMeasures],
      [] as any
    )
    log('uniqueMeans', uniqueMeans)

    if (isEdit) {
      if (riskMap?.id) {
        update({
          ...commonForm,
          id: riskMap.id,
        })
      }
    } else {
      create(commonForm)
    }
  }

  const topTabs: TabsItem[] = [
    {
      title: 'Основные',
      badgeColor: hasValidationErrors(formState.errors, [
        'company',
        'filial',
        'division',
        'position',
        'number',
      ])
        ? 'red'
        : undefined,
      children: <MainRisks status={riskMap?.status} isEdit={isEdit} />,
      id: '1',
    },
    {
      title: 'Опасности и риски',
      badgeColor: hasValidationErrors(formState.errors, ['dangerDTOs'])
        ? 'red'
        : undefined,
      children: <Dangers />,
      id: 'dangers-tab',
    },
  ]

  return (
    <AddNewItemModalLayout
      {...additionalProps}
      handleCloseModal={onModalClose}
      onSubmitForm={handleSubmitForm}
      methods={methods}
      isEdit={isEdit}
      titlePostfix="карту рисков"
      successContentAddTitle="Карта рисков успешно добавлена"
      successContentEditTitle="Карта рисков успешно изменена"
      withStopPropagation
      onSaveAndAddMore={() => {
        dispatch(cleanAllForms())
      }}
    >
      <TopControllersContext.Provider
        value={{
          enabledEtn,
          enabledAutoAddSiz,
          toggleEnabledEtn,
          toggleEnabledAutoAddSiz,
        }}
      >
        {!isEdit && (
          <Onboarding
            firstStepTitle={ONBOARDING_TITLE}
            steps={getOnboardingSteps(() => {
              setActive(1)
            })}
            code={OnboardingCodes.risk_map_create}
            withScroll
            withFeedback
          />
        )}
        <TopTabsWrapper>
          <Tabs
            withNoUnmount
            items={topTabs}
            active={active}
            setActive={setActive}
            tabSize="small"
          />
        </TopTabsWrapper>
      </TopControllersContext.Provider>
    </AddNewItemModalLayout>
  )
}

export default RiskMapModal
